import { isNil } from 'lodash';
import React from 'react';
import { GridCellProps } from 'react-virtualized';
import { colors } from '../../../../config/styles';
import { formatNumberWithComma } from '../../../../utils/formatText';
import {
  useTableColumnWidthState,
  useTableColumnSortState,
} from '../../../MultiGridTable';
import { ITableSettings } from '../../interfaces';
import * as ChartDataTableUI from '../ChartDataTableUI';
import { useChartDataTable } from '../useChartDataTable';
import {
  calculateMeasureIndex,
  getColumnAggregatedValue,
  renderPercentageText,
} from '../utils';
import { TableSortIcon } from '../../../Icon';
import { Order } from '../../../../utils/apollo/graphqlSchema';
import KubitTooltip from '../../../KubitTooltip';

interface IProps {
  gridCellProps: GridCellProps;
  dataTableData: ReturnType<typeof useChartDataTable>;
  columnWidthsData: ReturnType<typeof useTableColumnWidthState>;
  columnSortStateData: ReturnType<typeof useTableColumnSortState>;
  tableSettings: ITableSettings;
}

const BreakdownHeaderRenderer = ({
  gridCellProps,
  dataTableData,
  columnWidthsData,
  columnSortStateData,
  tableSettings,
}: IProps) => {
  const { columnIndex, rowIndex, key, style } = gridCellProps;
  const { breakdowns, segments, hasComparison, measures, rows } = dataTableData;
  const { handleMouseDown } = columnWidthsData;
  const { sortColumnIndex, sortDirection, onSort } = columnSortStateData;

  // if there are breakdowns, render an empty cell across all breakdowns
  if (columnIndex < breakdowns.length) {
    const breakdownName = breakdowns[columnIndex].field.name;
    const isColumnSorted = sortColumnIndex === columnIndex;
    return (
      <ChartDataTableUI.HeaderCell
        key={key}
        style={style}
        $columnIndex={columnIndex}
        $rowIndex={rowIndex}
        $isBackgroundFilled={true}
        title={breakdownName}
      >
        <ChartDataTableUI.SortableContainer
          onClick={() => onSort({ index: columnIndex })}
        >
          <KubitTooltip
            content={sortDirection === Order.Asc ? 'Ascending' : 'Descending'}
            disabled={!isColumnSorted}
            placement="bottom"
          >
            <ChartDataTableUI.SortableTitle $isColumnSorted={isColumnSorted}>
              {breakdownName}
            </ChartDataTableUI.SortableTitle>
          </KubitTooltip>
          {isColumnSorted && !isNil(sortDirection) && (
            <TableSortIcon isSortAsc={sortDirection === Order.Asc} />
          )}
        </ChartDataTableUI.SortableContainer>

        <ChartDataTableUI.ChartColumnResizeHandle
          onMouseDown={(e) => handleMouseDown(e, columnIndex)}
        />
      </ChartDataTableUI.HeaderCell>
    );
  }

  const colIndex = columnIndex - breakdowns.length;

  const numComparisonColumns = tableSettings.showPercentDelta ? 3 : 2;
  const measureIndex = calculateMeasureIndex(
    colIndex,
    segments,
    hasComparison,
    numComparisonColumns,
  );
  const measure = measures[measureIndex];

  // only show the sum if a breakdown is included in the query
  if (measure?.__typename === 'SimpleMeasure' && breakdowns.length > 0) {
    // if not compound measure, get the aggregate sum of each column
    // to display in the header
    const columnTotalValue = getColumnAggregatedValue(
      rows,
      columnIndex,
      measure.function.key,
    );

    if (
      hasComparison &&
      tableSettings.showPercentDelta &&
      colIndex % numComparisonColumns === 2
    ) {
      const previousColumnTotalValue = getColumnAggregatedValue(
        rows,
        columnIndex - 2,
        measure.function.key,
      );
      const currentColumnTotalValue = getColumnAggregatedValue(
        rows,
        columnIndex - 1,
        measure.function.key,
      );

      if (!isNil(previousColumnTotalValue) && !isNil(currentColumnTotalValue)) {
        const percentDelta =
          ((currentColumnTotalValue - previousColumnTotalValue) /
            previousColumnTotalValue) *
          100;
        const percentageText = renderPercentageText(percentDelta);
        const color =
          percentDelta > 0 ? 'green' : percentDelta < 0 ? 'red' : 'inherit';

        const backgroundColor =
          percentDelta > 0
            ? colors.backgroundGreen // light green shade
            : percentDelta < 0
              ? colors.backgroundRed // light red shade
              : 'inherit'; // no shade for 0

        return (
          <ChartDataTableUI.HeaderCell
            key={key}
            style={{
              ...style,
              color,
              backgroundColor,
            }}
            $columnIndex={columnIndex}
            $rowIndex={rowIndex}
            $isRightAligned={true}
          >
            {percentageText}
          </ChartDataTableUI.HeaderCell>
        );
      }
    }

    if (!isNil(columnTotalValue)) {
      return (
        <ChartDataTableUI.HeaderCell
          key={key}
          style={style}
          $columnIndex={columnIndex}
          $rowIndex={rowIndex}
          $isRightAligned={true}
        >
          {formatNumberWithComma(columnTotalValue)}
        </ChartDataTableUI.HeaderCell>
      );
    }
  }

  // return empty cell for header if compound measure
  return (
    <ChartDataTableUI.HeaderCell
      key={key}
      style={style}
      $columnIndex={columnIndex}
      $rowIndex={rowIndex}
    />
  );
};

export default BreakdownHeaderRenderer;
